import React, { useCallback } from 'react';
import { gql, useMutation } from '@nerdwallet/apollo';
import { Box } from '@nerdwallet/currency/components/Box';
import { Button } from '@nerdwallet/currency/components/Button';
import { Link } from '@nerdwallet/currency/components/Link';
import { Text } from '@nerdwallet/currency/components/Text';
import { Title } from '@nerdwallet/currency/components/Title';
import { datadogRum } from '@datadog/browser-rum';
import DialogContentWithCloseButton from '@components/mui/dialog-content-with-close-button';
import { financialPortalsQuery } from './queries';
import { usePlaidModalVisibility } from './plaid-modal-visibility-context';

export const unlinkFinancialInstitutionMutation = gql`
  mutation DeleteFinancialPortal($financialPortalId: ID!) {
    deleteFinancialPortal(financialPortalId: $financialPortalId) {
      success
    }
  }
`;

type PlaidUnlinkProps = {
  handleCloseModal: () => void;
  plaidUnlinkedAccounts: () => void;
};

const PlaidUnlink = ({
  handleCloseModal,
  plaidUnlinkedAccounts,
}: PlaidUnlinkProps) => {
  const { showPlaidVisibility } = usePlaidModalVisibility();

  const [unlinkInstitutionMutation, { loading }] = useMutation(
    unlinkFinancialInstitutionMutation,
    {
      refetchQueries: [{ query: financialPortalsQuery }],
      onCompleted: () => {
        plaidUnlinkedAccounts();
      },
      onError: (error) => {
        datadogRum.addError(
          'Plaid Modal - Unable to unlink financial institution',
          error,
        );
      },
      fetchPolicy: 'network-only',
    },
  );

  const handleUnlinkClick = useCallback(async () => {
    await unlinkInstitutionMutation({
      variables: {
        financialPortalId: showPlaidVisibility.selectedPortal?.id,
      },
    });
  }, [showPlaidVisibility.selectedPortal?.id, unlinkInstitutionMutation]);

  return (
    <DialogContentWithCloseButton onClose={handleCloseModal}>
      <Box display="flex" flexDirection="column" padding="1">
        <Title size="small">
          Unlink{' '}
          {showPlaidVisibility.selectedPortal?.marketplaceInstitution?.name}?
        </Title>
        <Box marginY="3-half">
          <Text>
            This will also unlink all accounts and transactions from this
            financial institution from NerdWallet
          </Text>
        </Box>
        <Button
          onClick={handleUnlinkClick}
          primary
          disabled={loading}
          loading={loading}
        >
          unlink account
        </Button>
        <Box marginTop="3" display="flex" justifyContent="center">
          <Link onClick={handleCloseModal}>Cancel</Link>
        </Box>
      </Box>
    </DialogContentWithCloseButton>
  );
};

export default PlaidUnlink;
